/**
 * Mobile Portal - Main App Component
 *
 * Purpose: Portal for SmartPay mobile/field teams (per PRD §6): view mobile units,
 * ATMs; log activity (inspection, repair); log maintenance/replenishment. Not agents.
 * Location: mobile-portal/src/App.tsx
 * Domain: mobile.ketchup.cc (*.ketchup.cc)
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@smartpay/ui';
import { AuthProvider, useAuth } from './contexts/AuthContext';

import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import FieldHub from './pages/FieldHub';
import ATMs from './pages/ATMs';
import Settings from './pages/Settings';
import Help from './pages/Help';
import NotFound from './pages/NotFound';
import Login from './pages/Login';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Toaster />
      <AuthProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="field" element={<Navigate to="/field/units" replace />} />
              <Route path="field/units" element={<FieldHub />} />
              <Route path="field/requests" element={<FieldHub />} />
              <Route path="field/map" element={<FieldHub />} />
              <Route path="field/activity" element={<FieldHub />} />
              <Route path="mobile-units" element={<Navigate to="/field/units" replace />} />
              <Route path="requests" element={<Navigate to="/field/requests" replace />} />
              <Route path="map" element={<Navigate to="/field/map" replace />} />
              <Route path="activity" element={<Navigate to="/field/activity" replace />} />
              <Route path="atms" element={<ATMs />} />
              <Route path="settings" element={<Settings />} />
              <Route path="help" element={<Help />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
